import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { AuthService } from '@merchant-portal/app/core/auth.service';
import { OtpApiService } from '@merchant-portal/app/core/otp/services/otp.api.service';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { ButtonComponent, InputModule } from 'ui-component/src/public-api';

interface TotpEnrollmentData {
  qrCodeDataUrl: string;
  secretKey: string;
}

@Component({
  selector: 'app-authenticator-setup-dialog',
  standalone: true,
  imports: [CommonModule, FormsModule, ButtonComponent, InputModule],
  template: `
    <div class="authenticator-setup-content">
      <!-- Success Message Screen -->
      <div *ngIf="showSuccessMessage" class="success-screen">
        <div class="icon-container">
          <img src="assets/icon/pin-successfull.svg" alt="Success Icon" class="success-icon" />
        </div>
        <h2 class="success-title">Google Authenticator Enabled Successfully</h2>
        <p class="success-message">
          Use the 6-digit code from Google Authenticator whenever you log in or confirm
          transactions.
        </p>
        <lib-button type="Primary" (click)="closeSuccess()" label="Close"></lib-button>
      </div>

      <!-- TOTP Already Active Screen -->
      <div *ngIf="totpAlreadyActive && !showSuccessMessage" class="already-active-screen">
        <div class="icon-container">
          <img src="assets/icon/pin-successfull.svg" alt="Success Icon" class="success-icon" />
        </div>
        <h2 class="success-title">Google Authenticator Enabled Successfully</h2>
        <p class="success-message">
          Use the 6-digit code from Google Authenticator whenever you log in or confirm
          transactions.
        </p>
        <lib-button type="Primary" (click)="closeSuccess()" label="Close"></lib-button>
      </div>

      <!-- Setup Screen -->
      <div *ngIf="!showSuccessMessage && !totpAlreadyActive" class="setup-screen">
        <div class="setup-header">
          <h2>Authenticator App</h2>
        </div>

        <div class="setup-steps">
          <div class="step">
            <span class="step-number">1.</span>
            <p>
              Download & open authenticator app such as Google Authenticator, Microsoft
              Authenticator, or Authy on your mobile phone
            </p>
          </div>

          <div class="step">
            <span class="step-number">2.</span>
            <p>Click (+) and scan this QR Code</p>
          </div>

          <div class="qr-section" *ngIf="totpEnrollmentData">
            <div class="qr-code">
              <img [src]="totpEnrollmentData.qrCodeDataUrl" alt="QR Code" class="qr-image" />
            </div>

            <div class="manual-entry">
              <p class="manual-text">Can't scan the QR code?</p>
              <p class="manual-link">Enter the key manually</p>

              <div class="manual-key">
                <div class="key-container">
                  <span class="secret-key">{{ totpEnrollmentData.secretKey }}</span>
                  <i
                    class="pi pi-copy copy-icon"
                    (click)="copySecretKey()"
                    title="Copy to clipboard"
                  ></i>
                </div>
              </div>
            </div>
          </div>

          <div class="step">
            <span class="step-number">3.</span>
            <p>Enter 6-digit code from your authenticator app <span class="required">*</span></p>
          </div>
          <lib-input
            type="text"
            [(ngModel)]="authenticatorCode"
            placeholder="Input code"
            (input)="onAuthenticatorCodeChange($event)"
            maxlength="6"
            [error]="confirmationError"
          ></lib-input>
        </div>

        <div class="setup-buttons">
          <lib-button type="Secondary" (click)="cancel()" label="Cancel"> </lib-button>
          <lib-button
            type="Primary"
            [disabled]="!isValidAuthenticatorCode()"
            (click)="verifyAuthenticatorCode()"
            label="Verify Code"
          >
          </lib-button>
        </div>
      </div>
    </div>
  `,
  styleUrls: ['./authenticator-setup-dialog.component.scss'],
})
export class AuthenticatorSetupDialogComponent implements OnInit {
  totpEnrollmentData: TotpEnrollmentData | null = null;
  authenticatorCode = '';
  showSuccessMessage = false;
  totpAlreadyActive = false;
  confirmationError = '';

  constructor(
    private authService: AuthService,
    private dialogRef: DynamicDialogRef,
    private dialogConfig: DynamicDialogConfig,
    private otpApiService: OtpApiService
  ) {}

  ngOnInit() {
    // Check if enrollment data was passed to the dialog
    if (this.dialogConfig?.data) {
      this.totpEnrollmentData = this.dialogConfig.data;
    } else {
      this.enrollTOTP();
    }
  }

  enrollTOTP() {
    this.authService.enrollTOTP().subscribe({
      next: response => {
        if (response?.data) {
          this.totpEnrollmentData = response.data;
        }
      },
      error: error => {
        // Check if TOTP is already activated
        if (error?.error?.code === '48' || error?.error?.message === 'TOTP has been activated') {
          this.totpAlreadyActive = true;
        }
      },
    });
  }

  onAuthenticatorCodeChange(event: any) {
    this.authenticatorCode = event.target.value;
    // Clear any previous error when user starts typing
    this.confirmationError = '';
  }

  isValidAuthenticatorCode(): boolean {
    return this.authenticatorCode.length === 6 && /^\d+$/.test(this.authenticatorCode);
  }

  copySecretKey() {
    if (this.totpEnrollmentData?.secretKey) {
      navigator.clipboard.writeText(this.totpEnrollmentData.secretKey).then(() => {
        // Secret key copied to clipboard
        // You could show a toast notification here
      });
    }
  }

  verifyAuthenticatorCode() {
    if (!this.isValidAuthenticatorCode()) {
      return;
    }

    // Use the confirmTOTP endpoint for setup verification
    this.authService.confirmTOTP(this.authenticatorCode).subscribe({
      next: response => {
        // Update user info to set TOTP status as ACTIVE
        const userInfo = this.authService.getUserInfo();
        if (userInfo) {
          userInfo.totpStatus = 'ACTIVE';
          this.authService.setUserInfo(userInfo);
        }

        // Show success message
        this.showSuccessMessage = true;

        // Close dialog after a delay to show the success message
        setTimeout(() => {
          this.dialogRef.close({ action: 'verified', data: response });
        }, 2000);
      },
      error: error => {
        console.error('TOTP confirmation error:', error);
        // Display error message below the input
        this.confirmationError =
          error?.error?.message || error?.error?.errors || 'Verification failed. Please try again.';
      },
    });
  }

  cancel() {
    this.dialogRef.close({ action: 'cancel' });
  }

  closeSuccess() {
    if (this.totpAlreadyActive) {
      this.dialogRef.close({ action: 'totp_already_active' });
    } else {
      this.dialogRef.close({ action: 'verified' });
    }
  }
}
